import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicOpenScheduleDto, PublicSchoolDto } from '@motoschool/shared';
import { DEFAULT_LESSON_MINUTES, formatMoney } from '@motoschool/shared';
import { api } from '../lib/api';
import { Button, Card, EmptyState, PageLoader, cn } from '../components/ui';
import { PublicTimePicker, type PickedLesson } from '../components/PublicTimePicker';
import { SchoolName } from '../components/SchoolName';
import { Logo } from '../components/Logo';
import { GuestBookingSheet } from '../components/GuestBookingSheet';
import { usePageMeta } from '../lib/seo';
import { SCHEDULE_META } from '../lib/routeMeta';

/**
 * Главная страница расписания. Пока запись открыта, свободное время видно
 * всем и заявку можно отправить без регистрации — это основной путь новых
 * учеников. Администратор закрывает её в настройках школы, и тогда адрес
 * уводит на главную: время согласуют по телефону.
 *
 * Личная ссылка инструктора (`/s/:token`) этим переключателем не закрыта —
 * он раздаёт её сам и сам решает, кого записывает.
 */
export function SchedulePublicPage(): React.JSX.Element {
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedLesson | null>(null);
  const [isSent, setIsSent] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['public', 'schedule'],
    queryFn: () =>
      api.get<PublicOpenScheduleDto>('/public/schedule', undefined, { anonymous: true }),
  });

  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
  });

  usePageMeta({ ...SCHEDULE_META, path: '/schedule' });

  const teachers = data?.teachers ?? [];
  const activeTeacher = teacherId ? teachers.find((item) => item.id === teacherId) : null;

  /*
   * Без фильтра в списке смешано время всех инструкторов, и два одинаковых
   * «09:00» рядом неразличимы — гость выбирал бы вслепую. Поэтому в общем
   * списке к каждому времени прикладывается имя, а у выбранного инструктора нет.
   */
  const visible = useMemo(() => {
    const source = activeTeacher ? [activeTeacher] : teachers;
    const withName = source.length > 1;

    return source.map((teacher) => ({
      teacherId: teacher.id,
      teacherName: withName ? teacher.name : undefined,
      hourlyRateMinor: teacher.hourlyRateMinor,
      free: teacher.free,
      busy: teacher.busy,
    }));
  }, [teachers, activeTeacher]);

  const timeZone = data?.timeZone ?? 'Asia/Almaty';
  const currency = data?.currency ?? 'KZT';

  if (isLoading) return <PageLoader />;

  /*
   * Ждём ответа про настройки: до него неизвестно, открыта ли запись, а
   * мигнуть расписанием и увести на главную — хуже, чем показать загрузку.
   */
  if (!school) return <PageLoader />;
  if (!school.isBookingOpen) return <Navigate to="/" replace />;

  return (
    <div className="min-h-dvh bg-canvas">
      <header className="safe-top border-b border-line bg-surface px-4 pb-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          {/* Название ведёт на главную: иначе с расписания некуда вернуться */}
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <Logo className="size-11" />
            <span className="min-w-0">
              <SchoolName
                name={data?.schoolName ?? 'Мотошкола'}
                className="block truncate text-lg"
              />
              <span className="block text-sm text-muted">Запись на занятие</span>
            </span>
          </Link>
          <Link to="/login">
            <Button variant="secondary">Войти</Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-5">
        {isSent && (
          <div className="rounded-card border border-emerald-200 bg-emerald-50 p-4">
            <p className="font-medium text-emerald-800">Заявка отправлена</p>
            <p className="mt-1 text-sm text-emerald-700">
              Инструктор подтвердит запись и свяжется с вами. Вместе с подтверждением он
              заведёт вам аккаунт и передаст пароль — в личном кабинете будут видны занятия,
              баланс часов и оплаты.
            </p>
          </div>
        )}

        <div>
          <h1 className="text-xl font-semibold">Свободное время</h1>
          <p className="text-sm text-muted">
            Выберите день, начало и сколько часов ездить — регистрация не нужна
          </p>
        </div>

        {teachers.length === 0 ? (
          <EmptyState
            title="Расписание пока не заполнено"
            description="Инструкторы ещё не задали часы работы. Загляните позже."
          />
        ) : (
          <>
            {/* Фильтр по инструктору: при одном инструкторе он не нужен */}
            {teachers.length > 1 && (
              <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
                <button
                  type="button"
                  onClick={() => setTeacherId(null)}
                  className={cn(
                    'min-h-10 shrink-0 rounded-full border px-4 text-sm font-medium',
                    teacherId === null
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-line bg-surface text-muted',
                  )}
                >
                  Все инструкторы
                </button>
                {teachers.map((teacher) => (
                  <button
                    key={teacher.id}
                    type="button"
                    onClick={() => setTeacherId(teacher.id)}
                    className={cn(
                      'flex min-h-10 shrink-0 items-center rounded-full border px-4 text-sm font-medium',
                      teacherId === teacher.id
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-line bg-surface text-muted',
                    )}
                  >
                    {teacher.name}
                  </button>
                ))}
              </div>
            )}

            {activeTeacher && activeTeacher.hourlyRateMinor !== null && (
              <Card className="p-3">
                <p className="text-sm">
                  <b>{activeTeacher.name}</b> —{' '}
                  {formatMoney(activeTeacher.hourlyRateMinor, currency)} за час занятий
                </p>
              </Card>
            )}

            <PublicTimePicker
              teachers={visible}
              timeZone={timeZone}
              currency={currency}
              defaultMinutes={school?.defaultLessonMinutes ?? DEFAULT_LESSON_MINUTES}
              onPick={(lesson) => {
                setIsSent(false);
                setPicked(lesson);
              }}
            />
          </>
        )}

        <Card className="bg-brand-50/60">
          <p className="text-sm">
            Уже занимаетесь здесь?{' '}
            <Link to="/login" className="font-medium text-brand-600">
              Войдите
            </Link>{' '}
            — увидите свои занятия, баланс оплаченных часов и историю оплат.
          </p>
        </Card>
      </main>

      <GuestBookingSheet
        lesson={picked}
        endpoint="/public/requests"
        timeZone={timeZone}
        currency={currency}
        onClose={() => setPicked(null)}
        onSuccess={() => {
          setPicked(null);
          setIsSent(true);
        }}
      />
    </div>
  );
}
